import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useEffect, useState } from "react";
import HeroSlideshow from "@/components/HeroSlideshow";
import { listHeroMedia, absoluteUrl, listSiteContent, fetchMenuTree } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSwipeToNavigate } from "@/components/MobilePageDots";

const DEFAULT_PARALLAX =
  "https://images.unsplash.com/photo-1548943487-a2e4e43b4853?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

// Subtle placeholder shown in the Story section when no image/video has
// been uploaded for a slot — deliberately quiet so the layout still holds
// without pulling in random stock imagery.
function StoryMediaPlaceholder({ label }) {
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/45 gap-2 text-center px-3"
      data-testid={`home-story-media-placeholder-${(label || "").toLowerCase()}`}
    >
      <div className="text-primary/40 text-3xl leading-none">✦</div>
      <div className="text-[0.62rem] uppercase tracking-[0.22em]">
        {label || "Media"}
      </div>
      <div className="text-[0.62rem] italic normal-case tracking-normal opacity-70">
        Upload in admin
      </div>
    </div>
  );
}

// Card displayed in the "On The Menu" section for each visible menu group.
// Shows the group's label + tagline over an optional background image or video.
function MenuCategoryCard({ group, index }) {
  const mediaUrl = group.video_url || group.image_url;
  const isVideo = !!group.video_url;
  const bgUrl = mediaUrl ? absoluteUrl(mediaUrl) : null;
  const categoryLabel = group.label || group.name || group.id || "";

  return (
    <Link
      to={`/menus?category=${encodeURIComponent(categoryLabel)}`}
      data-testid={`home-menu-card-${index}`}
      className="group relative overflow-hidden border border-white/10 bg-background/60 backdrop-blur-md p-8 min-h-[16rem] flex flex-col justify-end transition-[transform,border-color] duration-500 hover:-translate-y-1 hover:border-primary/60"
    >
      {bgUrl && (
        <div className="absolute inset-0 overflow-hidden">
          {isVideo ? (
            <video
              src={bgUrl}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity duration-700 scale-[1.02] group-hover:scale-105"
              data-testid={`home-menu-card-${index}-video`}
            />
          ) : (
            <img
              src={bgUrl}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-80 transition-[opacity,transform] duration-700 scale-[1.02] group-hover:scale-105"
              data-testid={`home-menu-card-${index}-image`}
            />
          )}
          {/* Readability veil */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/70 to-background/20" />
        </div>
      )}
      <div className="relative">
        <div className="text-primary text-lg">✦</div>
        <div className="mt-6 serif text-3xl font-light text-foreground group-hover:text-primary transition-colors">
          {group.label}
        </div>
        {group.tagline && (
          <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
            {group.tagline}
          </div>
        )}
      </div>
    </Link>
  );
}

// Countdown target — driven by site settings (fallback used until settings load)
const DEFAULT_OPENING = new Date("2026-08-19T18:00:00+07:00");

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs };
}

export default function Home() {
  const { settings } = useSettings();
  const openingDate = (() => {
    try {
      return settings.opening_at ? new Date(settings.opening_at) : DEFAULT_OPENING;
    } catch {
      return DEFAULT_OPENING;
    }
  })();
  const { days, hours, mins, secs } = useCountdown(openingDate);
  const [parallaxUrl, setParallaxUrl] = useState(DEFAULT_PARALLAX);
  const [content, setContent] = useState({});
  const [menuGroups, setMenuGroups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listHeroMedia(true, "home-parallax");
        if (cancelled) return;
        const first = list[0];
        if (first?.url) setParallaxUrl(absoluteUrl(first.url) || DEFAULT_PARALLAX);
      } catch (err) {
        console.warn("[Home] Failed to load parallax hero — using default", err);
      }
      try {
        const items = await listSiteContent();
        if (cancelled) return;
        const map = {};
        items.forEach((c) => (map[c.section] = c));
        setContent(map);
      } catch (err) {
        console.warn("[Home] Failed to load site content — using defaults", err);
      }
      try {
        const tree = await fetchMenuTree();
        if (cancelled) return;
        // Public /api/menus already returns only visible groups.
        setMenuGroups(Array.isArray(tree) ? tree : []);
      } catch (err) {
        console.warn("[Home] Failed to load menu tree", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hero = content["home-hero"] || {};
  const story = content["home-story"] || {};
  const chef = content["home-chef"] || {};
  const teaser = content["home-menu-teaser"] || {};
  const reserve = content["home-reserve"] || {};
  const swipeHandlers = useSwipeToNavigate();

  return (
    <div data-testid="page-home" {...swipeHandlers} className="touch-pan-y">
      {/* ===================== HERO ===================== */}
      <section
        data-testid="home-hero"
        className="relative min-h-screen flex items-end overflow-hidden grain"
      >
        <HeroSlideshow />

        <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 lg:px-12 pb-24 pt-40">
          <div className="max-w-3xl fade-up">
            <div className="overline mb-8" data-testid="home-opening-label">{settings.opening_label || "Opening 19 August 2026 · Senopati, Jakarta"}</div>
            {hero.title_visible !== false && (
              <h1 className="serif text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-foreground [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]" data-testid="home-hero-title">
                {hero.title_top || "Modern"}
                <br />
                <span className="italic text-foreground">{hero.title_italic || "Sri Lankan"}</span>
                <br />
                {hero.title_bottom || "Cuisine."}
              </h1>
            )}
            {hero.body_visible !== false && (
              <p className="mt-10 text-base sm:text-lg leading-relaxed text-foreground/90 max-w-xl [text-shadow:0_1px_10px_rgba(0,0,0,0.5)] whitespace-pre-line" data-testid="home-hero-body">
                {hero.body ||
                  "A story of spice, coast, and memory. Taste the soul of Sri Lanka — served slowly, together, in the heart of Senopati."}
              </p>
            )}

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <Link
                to={hero.cta_url || "/reservation"}
                data-testid="home-hero-cta-reserve"
                className="group inline-flex items-center gap-3 px-7 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 transition-colors"
              >
                {hero.cta_label || "Reserve a Table"}
                <ArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/menus"
                data-testid="home-hero-cta-menu"
                className="inline-flex items-center gap-3 px-7 py-4 border border-border text-foreground text-[0.72rem] uppercase tracking-[0.24em] font-medium hover:border-primary hover:text-primary transition-colors"
              >
                Explore the Menus
              </Link>
            </div>
          </div>

          <div className="mt-16 flex items-end justify-between border-t border-border pt-6">
            {settings.countdown_enabled !== false ? (
              <div
                data-testid="home-countdown"
                className="flex items-baseline gap-6 text-foreground/90"
              >
                {[
                  ["Days", days],
                  ["Hrs", hours],
                  ["Min", mins],
                  ["Sec", secs],
                ].map(([label, val]) => (
                  <div key={label} className="flex items-baseline gap-1.5">
                    <span className="serif text-3xl md:text-4xl font-light tabular-nums text-primary">
                      {String(val).padStart(2, "0")}
                    </span>
                    <span className="overline text-muted-foreground">{label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div />
            )}
            <button
              type="button"
              data-testid="hero-scroll-button"
              onClick={() =>
                document
                  .getElementById("home-story-anchor")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              aria-label="Scroll to Our Story"
              className="hidden md:flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-[0.22em] hover:text-primary transition-colors group cursor-pointer"
            >
              Scroll <ArrowDown size={12} className="animate-bounce group-hover:text-primary" />
            </button>
          </div>
        </div>
      </section>

      {/* ===================== 01 — STORY ===================== */}
      <section
        id="home-story-anchor"
        data-testid="home-story"
        className="relative py-32 lg:py-40 border-t border-border scroll-mt-24"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5 lg:col-start-1">
            <div className="overline">{story.overline || "01 — Our Story"}</div>
            <h2 className="mt-8 serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-foreground">
              {story.title_top || "The lotus rises"}
              <br /> {story.title_bottom || "from muddy water —"} <br />
              <span className="italic text-primary">{story.title_italic || "to bloom in pure beauty."}</span>
            </h2>
            <div className="mt-10 space-y-6 text-sm leading-relaxed text-muted-foreground max-w-md">
              <p>{story.body || "NELUM is Jakarta's first modern Sri Lankan restaurant — bringing the vibrant spices, coastal influences, and multicultural flavours of the island to Senopati."}</p>
              {(story.body_secondary || !story.body) && (
                <p>{story.body_secondary || "Led by a Sri Lankan chef with over twenty years of international craft, every dish is both a memory and a discovery. A story in every bite — inspired by the island of Ceylon."}</p>
              )}
            </div>
            <Link
              to={story.cta_url || "/menus"}
              data-testid="home-story-cta"
              className="mt-10 inline-flex items-center gap-3 text-primary text-[0.72rem] uppercase tracking-[0.24em] font-semibold group"
            >
              <span className="link-underline">{story.cta_label || "Read the Menus"}</span>
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-6 gap-4 lg:gap-6">
            <div className="col-span-4 aspect-[4/5] overflow-hidden border border-white/5 bg-card/40">
              {story.video_primary ? (
                <video
                  src={absoluteUrl(story.video_primary)}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-105"
                  data-testid="home-story-media-primary-video"
                />
              ) : story.image_primary ? (
                <img
                  src={absoluteUrl(story.image_primary)}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-[1400ms] hover:scale-105"
                  data-testid="home-story-media-primary-image"
                />
              ) : (
                <StoryMediaPlaceholder label="Primary" />
              )}
            </div>
            <div className="col-span-2 flex flex-col gap-4 lg:gap-6">
              <div className="aspect-square overflow-hidden border border-white/5 bg-card/40">
                {story.video_secondary ? (
                  <video
                    src={absoluteUrl(story.video_secondary)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                    data-testid="home-story-media-secondary-video"
                  />
                ) : story.image_secondary ? (
                  <img
                    src={absoluteUrl(story.image_secondary)}
                    alt=""
                    className="w-full h-full object-cover"
                    data-testid="home-story-media-secondary-image"
                  />
                ) : (
                  <StoryMediaPlaceholder label="Secondary" />
                )}
              </div>
              <div className="flex-1 border border-border p-6 flex flex-col justify-between bg-card relative overflow-hidden">
                {(story.video_tertiary || story.image_tertiary) && (
                  <>
                    {story.video_tertiary ? (
                      <video
                        src={absoluteUrl(story.video_tertiary)}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                        data-testid="home-story-quote-video"
                      />
                    ) : (
                      <img
                        src={absoluteUrl(story.image_tertiary)}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                        data-testid="home-story-quote-image"
                      />
                    )}
                    {/* Readability veil */}
                    <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/70 to-background/25" />
                  </>
                )}
                <div className="relative text-primary text-2xl leading-none">✦</div>
                <div className="relative">
                  <div className="overline">{story.quote_label || "Ceylon"}</div>
                  <div className="serif italic text-2xl text-foreground/95 mt-2 leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                    {story.quote_text || "A story in every bite."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== THE KITCHEN — CHEF PROFILE ===================== */}
      {(chef.chef_name || chef.image_primary || chef.body) && (
        <section
          data-testid="home-chef"
          className="relative py-24 lg:py-36 border-t border-border overflow-hidden"
        >
          {/* Subtle diagonal texture overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(135deg, hsl(var(--primary)) 0px, hsl(var(--primary)) 1px, transparent 1px, transparent 8px)",
            }}
          />
          <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Portrait */}
            <div className="lg:col-span-5 lg:col-start-1 order-1 lg:order-1">
              <div className="relative aspect-[3/4] max-w-md mx-auto lg:mx-0 overflow-hidden border border-border bg-card shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                {chef.image_primary ? (
                  <img
                    src={absoluteUrl(chef.image_primary)}
                    alt={chef.chef_name || "Chef portrait"}
                    className="w-full h-full object-cover"
                    data-testid="home-chef-image"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground italic text-sm px-8 text-center">
                    Upload the chef portrait via <br /> Admin → Content → The Kitchen
                  </div>
                )}
                {/* Signature name band */}
                {chef.chef_name && (
                  <div
                    data-testid="home-chef-name-band"
                    className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-5 bg-gradient-to-t from-background via-background/85 to-transparent"
                  >
                    <div>
                      <div className="serif italic text-xl md:text-2xl text-foreground leading-none">
                        {chef.chef_name}
                      </div>
                      {chef.chef_title && (
                        <div className="mt-1.5 text-[0.62rem] uppercase tracking-[0.24em] text-primary font-semibold">
                          {chef.chef_title}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Copy */}
            <div className="lg:col-span-6 lg:col-start-7 order-2 lg:order-2">
              <div className="overline" data-testid="home-chef-overline">
                {chef.overline || "The Kitchen"}
              </div>
              <h2 className="mt-6 serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight text-foreground">
                {chef.title_top || "Cooked by hand,"} <br />
                <span className="italic text-primary">
                  {chef.title_italic || "led by memory."}
                </span>
              </h2>

              {chef.quote_text && (
                <blockquote
                  data-testid="home-chef-quote"
                  className="mt-10 border-l-2 border-primary/60 pl-6 serif italic text-xl md:text-2xl text-foreground/90 leading-snug max-w-xl whitespace-pre-line"
                >
                  “{chef.quote_text}”
                </blockquote>
              )}

              {chef.body && (
                <p
                  data-testid="home-chef-body"
                  className="mt-8 text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl whitespace-pre-line"
                >
                  {chef.body}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ===================== 02 — ON THE MENU ===================== */}
      <section
        data-testid="home-menu-teaser"
        className="relative py-32 lg:py-40 border-t border-border"
        style={{
          backgroundImage: `linear-gradient(hsl(0 0% 5% / 0.85), hsl(0 0% 5% / 0.92)), url(${parallaxUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 lg:items-stretch">
            {/* Copy column */}
            <div className={`${teaser.image_primary ? "lg:col-span-6" : "lg:col-span-8"} min-w-0 flex flex-col`}>
              <div className="overline">{teaser.overline || "02 — On The Menu"}</div>
              <h2 className="mt-8 serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-foreground">
                {teaser.title_top || "Rice & curry,"} <br />
                <span className="italic text-primary">{teaser.title_italic || "reimagined."}</span>
              </h2>
              <p
                data-testid="home-menu-teaser-body"
                className="mt-8 text-sm md:text-base leading-relaxed text-foreground/85 whitespace-pre-line"
              >
                {teaser.body || "Seven accompaniments, each carrying the memory of a different coastal town. Layered, bold, deeply spiced — this is how Sri Lanka eats: together, generously, and without apology."}
              </p>
              {teaser.body_secondary && (
                <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground/70 whitespace-pre-line">
                  {teaser.body_secondary}
                </p>
              )}
            </div>

            {/* Image column — only when uploaded. Matches copy column height. */}
            {teaser.image_primary && (
              <div className="lg:col-span-6 min-h-[24rem]" data-testid="home-menu-teaser-image-wrap">
                <div className="w-full h-full overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
                  <img
                    src={absoluteUrl(teaser.image_primary)}
                    alt={teaser.title_top || "On The Menu"}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    data-testid="home-menu-teaser-image"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Active menu categories → one card per visible group with optional bg media */}
          {menuGroups.length > 0 && (
            <div
              className={`mt-16 grid gap-6 ${
                menuGroups.length === 1
                  ? "md:grid-cols-1"
                  : menuGroups.length === 2
                  ? "md:grid-cols-2"
                  : "md:grid-cols-3"
              }`}
              data-testid="home-menu-category-cards"
            >
              {menuGroups.map((g, i) => (
                <MenuCategoryCard key={g.id || i} group={g} index={i} />
              ))}
            </div>
          )}

          <div className="mt-14">
            <Link
              to={teaser.cta_url || "/menus"}
              data-testid="home-menu-teaser-cta"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-primary text-primary text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              {teaser.cta_label || "View Full Menus"}
              <ArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== 03 — RESERVE ===================== */}
      <section
        id="reserve"
        data-testid="home-reserve"
        className="relative py-24 md:py-28 lg:py-32 border-t border-border overflow-hidden bg-background"
      >
        <div className="mx-auto max-w-[1080px] px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {reserve.image_primary && (
            <div className="lg:col-span-7">
              <div className="aspect-[4/3] overflow-hidden border border-white/5">
                <img
                  src={absoluteUrl(reserve.image_primary)}
                  alt="Nelum interior candlelight"
                  className="w-full h-full object-cover transition-transform duration-1000 hover:scale-[1.02]"
                  data-testid="home-reserve-image"
                />
              </div>
            </div>
          )}
          <div className={reserve.image_primary ? "lg:col-span-5" : "lg:col-span-8 lg:col-start-3"}>
            <div className="overline text-[0.62rem] tracking-[0.28em]">{reserve.overline || "03 — Reserve"}</div>
            <h2 className="mt-6 serif text-4xl md:text-5xl font-light leading-[1.04] tracking-tight text-foreground">
              {reserve.title_top || "A table,"} <br />
              <span className="italic text-primary">{reserve.title_italic || "quietly held for you."}</span>
            </h2>
            <p className="mt-7 text-sm leading-relaxed text-muted-foreground max-w-md">
              {reserve.body || "Dinner service runs Tuesday to Sunday, from 18:00. We recommend booking a fortnight in advance for weekend service."}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={reserve.cta_url || "/reservation"}
                data-testid="home-reserve-cta"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-primary text-primary-foreground text-[0.65rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 transition-colors"
              >
                {reserve.cta_label || "Book a Table"}
              </Link>
              <Link
                to="/location"
                data-testid="home-reserve-location"
                className="inline-flex items-center justify-center px-6 py-3.5 border border-border text-foreground text-[0.65rem] uppercase tracking-[0.22em] font-medium hover:border-primary hover:text-primary transition-colors"
              >
                Find Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
